import {useParams} from 'next/navigation'
import {useCallback, useMemo} from 'react'

import {IcShare} from '@defign/ui'
import {useUserAgentContext} from '@financial/common-contexts'
import {Action, sendAULog} from '@financial/common-logs'
import {ShareButton} from '@pie/social-plugin'
import {useQuery} from '@tanstack/react-query'
import classNames from 'classnames/bind'
import DOMPurify from 'isomorphic-dompurify'

import InsuranceConnectBanner from '$components/insurance/common/InsuranceConnectBanner'
import {INTERNAL_ROUTES} from '$constants/internal-routes'
import useInsuranceMyAssetQuery from '$hooks/insurance/useInsuranceMyAssetQuery'
import {useInsuranceBannerHooks} from '$hooks/useInsuranceBannerHooks'
import {getContentQuery} from '$queries/insurance/contentQuery'
import {보험팁_카테고리_문자} from '$share/constants/insurance'
import {isRealProfile} from '$share/utils/env'
import buildUrl from '$utils/buildUrl'
import {sendClickNlog} from '$utils/nlog'

import InsuranceBanner from './InsuranceBanner'
import Styles from './InsuranceTipContent.module.scss'

const cx = classNames.bind(Styles)

const InsuranceTipContent = () => {
    const {isMobile} = useUserAgentContext()
    const {data} = useInsuranceMyAssetQuery()
    const {getBanner} = useInsuranceBannerHooks({hasValidInsurances: Boolean(data?.isMydataInsuranceConnected)})

    const {id} = useParams()

    const shareUrl = useMemo(() => {
        return buildUrl({pathname: INTERNAL_ROUTES['INSURANCE_CONTENTS_[ID]'], query: {id}})
    }, [id])

    const {data: contentData} = useQuery({
        ...getContentQuery.query({params: {contentId: +id}}),
    })

    const handleClick = useCallback(() => {
        sendClickNlog('insutipdetail.share')
        sendAULog({page: 'insu', area: 'D', service: 'md', code: 'insu_tip_detail_share'}, Action.Click)
    }, [])

    const Banner = getBanner(InsuranceConnectBanner)

    return (
        <div className={cx('article')}>
            <div className={cx('title-area')}>
                <span className={cx('category')}>
                    {contentData?.category ? 보험팁_카테고리_문자[contentData?.category] : ''}
                </span>

                <h3 className={cx('title')}>{contentData?.title}</h3>
                <div className={cx('keyword')}>
                    <strong className="blind">키워드 목록</strong>
                    <ul>
                        {(contentData?.keyword ?? []).map((keyword) => (
                            <li key={keyword} className={cx('tag')}>
                                #{keyword}
                            </li>
                        ))}
                    </ul>
                </div>
                <ShareButton
                    profile={isRealProfile ? 'real' : 'dev'}
                    env={isMobile ? 'mobile' : 'pc'}
                    pluginConfig={{
                        evKey: 'mydata_insurance',
                        serviceName: '마이데이터_보험_컨텐츠',
                        sourceName: '마이데이터 보험',
                        dimmed: 'fixed',
                    }}
                    elementConfig={{
                        style: isMobile ? 'one-button' : 'type_a',
                        url: shareUrl,
                        title: contentData?.title,
                        useShortUrl: 'on',
                    }}
                    className={cx('content-share')}
                    fallback={null}
                    onClick={handleClick}
                >
                    {/* 버튼 내부의 svg가 간헐적으로 button의 onClick 이벤트를 가로막음 */}
                    <IcShare width={16} height={16} style={{pointerEvents: 'none'}} id="insuTipShare" />
                    <span className="blind">컨텐츠 공유하기</span>
                </ShareButton>
                <InsuranceBanner />
                <Banner
                    bannerType={'contents-type'}
                    nClickCode="insutipdetail.regi"
                    auLogParam={{page: 'insu', service: 'md', area: 'D', code: 'insu_tip_detail_unrestered_my'}}
                />
            </div>
            <div className={cx('editor-contents')}>
                <div
                    dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(contentData?.mainText ?? '', {
                            ADD_TAGS: ['iframe'],
                        }),
                    }}
                    className={cx('ckeditor-content')}
                />
            </div>
        </div>
    )
}

export default InsuranceTipContent
